import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, useParams } from 'react-router';
import { makeStyles } from '@material-ui/core/styles';
import {
  Paper,
  Grid,
  TextField,
  Button,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { MdArrowBack, MdSave, MdArchive, MdUnarchive, MdControlPointDuplicate, MdDeleteForever } from 'react-icons/md';
import PageContainer from '../../../@jumbo/components/PageComponents/layouts/PageContainer';
import IntlMessages from '../../../@jumbo/utils/IntlMessages';
import axios from 'axios';
import { API_URL } from '../../../@jumbo/constants/AppConstants';
import cogoToast from 'cogo-toast';
import Swal from 'sweetalert2';
import { fetchAgenti } from '../../../redux/actions/Agenti';
import AllegatiProvvigione from './allegatiProvvigione';
import VersioniProvvigione from './versioniProvvigione';
import FornitoriTable from './fornitoriTable';
import ArticoliForm from './articoliForm';

const breadcrumbs = [
  { label: <IntlMessages id={'sidebar.main'} />, link: '/' },
  { label: 'Provvigioni', link: '/provvigioni' },
  { label: 'Provvigione', isActive: true },
];

const useStyles = makeStyles(theme => ({
  root: {
    flexGrow: 1,
  },
  paper: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  formControl: {
    width: '100%',
  },
  section: {
    marginBottom: theme.spacing(3),
  },
  sectionTitle: {
    marginBottom: theme.spacing(2),
    fontWeight: 'bold',
    color: theme.palette.primary.main,
  },
}));

export default function Provvigione() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const { pid } = useParams();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [allegati, setAllegati] = useState([]);
  const [fornitori, setFornitori] = useState([]);
  const [articoli, setArticoli] = useState([]);

  // Autocomplete states for cliente
  const [openCliente, setOpenCliente] = useState(false);
  const [optionsCliente, setOptionsCliente] = useState([]);
  const [valueCliente, setValueCliente] = useState(null);
  const [inputValueCliente, setInputValueCliente] = useState('');
  const [loadingCliente, setLoadingCliente] = useState(false);

  const [provvigione, setProvvigione] = useState({
    id: 0,
    numero: '',
    data_creazione: new Date().toISOString().split('T')[0],
    cliente: '',
    descrizione: '',
    totale_offerta: '',
    percentuale: '',
    totale_provvigione: '',
    agente_id: '',
    stato: 'ATTIVA',
    stato_new: '0',
    note: '',
    archiviata: 0,
    allegati: [],
  });

  // Load agenti for dropdown
  const { agenti } = useSelector(state => state.agenti);

  useEffect(() => {
    dispatch(fetchAgenti());
    loadData();
  }, [pid, dispatch]);

  // Load cliente options when user types
  useEffect(() => {
    if (inputValueCliente.length > 2) {
      (async () => {
        setLoadingCliente(true);
        try {
          const res = await axios.get(`${API_URL}/getNomiAnagrafiche.php?searchTerm=${inputValueCliente}`);
          const anagrafiche = res.data;
          setOptionsCliente(
            anagrafiche.map(key => ({
              name: key.RAGSOC,
              stato: key.CLFR === 'C' ? 'Cliente' : key.CLFR === 'F' ? 'Fornitore' : 'Contatto',
              id: key.CODCF,
              partiva: key.PARTIVA,
              codfisc: key.CODFISC,
              indir: key.INDIR,
              cap: key.CAP,
              local: key.LOCAL,
              prov: key.PROV,
              telefono: key.TEL,
              email: key.EMAIL,
            })),
          );
        } catch (error) {
          console.error('Error loading clienti:', error);
        }
        setLoadingCliente(false);
      })();
    }
  }, [inputValueCliente]);


  const loadData = async () => {
    try {
      setLoading(true);

      // If editing existing provvigione
      if (pid && pid !== 'new') {
        const provRes = await axios.get(`${API_URL}/getProvvigione.php?id=${pid}`);
        if (provRes.data.success && provRes.data.data) {
          const data = provRes.data.data;
          setProvvigione(data);
          // Load allegati if they exist
          if (data.allegati && Array.isArray(data.allegati)) {
            setAllegati(data.allegati);
          }
          // Load fornitori if they exist
          if (data.fornitori && Array.isArray(data.fornitori)) {
            setFornitori(data.fornitori);
          }
          // Load articoli if they exist
          if (data.articoli && Array.isArray(data.articoli)) {
            setArticoli(data.articoli);
          }
          // Initialize Autocomplete values with existing data
          if (data.cliente) {
            setInputValueCliente(data.cliente);
            setValueCliente(data.cliente);
          }
        }
      }

      setLoading(false);
    } catch (error) {
      console.error('Error loading data:', error);
      cogoToast.error('Errore nel caricamento dei dati');
      setLoading(false);
    }
  };

  const handleChange = e => {
    const { name, value } = e.target;
    setProvvigione(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSave = async () => {
    // Validation
    if (!provvigione.cliente || !provvigione.cliente.trim()) {
      cogoToast.error('Il cliente è obbligatorio');
      return;
    }
    if (!provvigione.descrizione || !provvigione.descrizione.trim()) {
      cogoToast.error('La descrizione è obbligatoria');
      return;
    }
    if (!provvigione.agente_id) {
      cogoToast.error('L\'agente è obbligatorio');
      return;
    }

    try {
      setSaving(true);
      // Include allegati, fornitori, and articoli in the save payload
      const dataToSave = {
        ...provvigione,
        allegati: allegati,
        fornitori: fornitori,
        articoli: articoli,
      };
      const response = await axios.post(`${API_URL}/SalvaProvvigione.php`, dataToSave);

      if (response.data.success) {
        cogoToast.success('Provvigione salvata con successo');

        // If it was a new provvigione, redirect to edit mode with the new ID
        if (!provvigione.id || provvigione.id === 0) {
          history.push(`/provvigione/${response.data.id}`);
        } else {
          // Reload data to get updated version
          loadData();
        }
      } else {
        cogoToast.error(response.data.error || 'Errore nel salvataggio');
      }

      setSaving(false);
    } catch (error) {
      console.error('Error saving:', error);
      cogoToast.error('Errore nel salvataggio della provvigione');
      setSaving(false);
    }
  };

  const handleVersiona = async () => {
    if (!provvigione.id || provvigione.id === 0) {
      cogoToast.error('Salva prima la provvigione');
      return;
    }

    const result = await Swal.fire({
      title: 'Crea Nuova Versione',
      text: 'Vuoi creare una nuova versione di questa provvigione?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sì, crea versione',
      cancelButtonText: 'Annulla',
    });

    if (result.isConfirmed) {
      try {
        setSaving(true);
        const response = await axios.post(`${API_URL}/VersionaProvvigione.php`, {
          id: provvigione.id,
          ...provvigione,
        });

        if (response.data.success) {
          Swal.fire('Successo', 'Nuova versione creata con successo', 'success');
          loadData();
        } else {
          cogoToast.error(response.data.error || 'Errore nella creazione della versione');
        }

        setSaving(false);
      } catch (error) {
        console.error('Error creating version:', error);
        cogoToast.error('Errore nella creazione della versione');
        setSaving(false);
      }
    }
  };

  const caricaProvvigione = revisioneData => {
    // Load a specific revision
    setProvvigione({
      ...revisioneData,
      id: provvigione.id, // Keep the original ID
    });
    if (revisioneData.allegati) {
      setAllegati(revisioneData.allegati);
    }
    cogoToast.info('Revisione caricata. Modifica e salva per creare una nuova versione.');
  };

  const handleArchive = async () => {
    if (!provvigione.id || provvigione.id === 0) {
      cogoToast.error('Salva prima la provvigione');
      return;
    }

    const isArchiving = provvigione.archiviata === 0;
    const result = await Swal.fire({
      title: isArchiving ? 'Archivia Provvigione' : 'Ripristina Provvigione',
      text: isArchiving
        ? 'Vuoi archiviare questa provvigione?'
        : 'Vuoi ripristinare questa provvigione?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: isArchiving ? 'Sì, archivia' : 'Sì, ripristina',
      cancelButtonText: 'Annulla',
    });

    if (result.isConfirmed) {
      try {
        setSaving(true);
        const endpoint = isArchiving ? 'ArchiviaProvvigione.php' : 'RipristinaProvvigione.php';
        const response = await axios.post(`${API_URL}/${endpoint}`, {
          id: provvigione.id,
        });

        if (response.data.success) {
          Swal.fire(
            'Successo',
            isArchiving ? 'Provvigione archiviata' : 'Provvigione ripristinata',
            'success',
          );
          loadData();
        } else {
          cogoToast.error(response.data.error || 'Errore nell\'operazione');
        }

        setSaving(false);
      } catch (error) {
        console.error('Error archiving:', error);
        cogoToast.error('Errore nell\'operazione');
        setSaving(false);
      }
    }
  };

  const handleDelete = async () => {
    if (!provvigione.id || provvigione.id === 0) {
      cogoToast.error('Salva prima la provvigione');
      return;
    }

    const result = await Swal.fire({
      title: 'Elimina Provvigione',
      text: 'Sei sicuro di voler eliminare questa provvigione? Questa azione è irreversibile!',
      icon: 'error',
      showCancelButton: true,
      confirmButtonText: 'Sì, elimina',
      confirmButtonColor: '#d33',
      cancelButtonText: 'Annulla',
    });

    if (result.isConfirmed) {
      try {
        setSaving(true);
        const response = await axios.post(`${API_URL}/eliminaProvvigione.php`, {
          id: provvigione.id,
        });

        if (response.data.success) {
          Swal.fire('Eliminata!', 'Provvigione eliminata con successo', 'success');
          history.push('/provvigioni');
        } else {
          cogoToast.error(response.data.error || 'Errore nell\'eliminazione');
          setSaving(false);
        }
      } catch (error) {
        console.error('Error deleting:', error);
        cogoToast.error('Errore nell\'eliminazione della provvigione');
        setSaving(false);
      }
    }
  };

  if (loading) {
    return (
      <PageContainer heading="Provvigione" breadcrumbs={breadcrumbs}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </PageContainer>
    );
  }

  return (
    <PageContainer heading="Provvigione" breadcrumbs={breadcrumbs}>
      <Box className={classes.root}>
        {/* Header with back button */}
        <Box mb={3}>
          <Button
            variant="outlined"
            startIcon={<MdArrowBack />}
            onClick={() => history.push('/provvigioni')}>
            Indietro
          </Button>
        </Box>

        {/* Versioni section - show only if there are revisioni */}
        {provvigione.id && provvigione.id > 0 && (
          <Paper className={classes.paper}>
            <VersioniProvvigione
              pid={provvigione.id}
              caricaProvvigione={caricaProvvigione}
            />
          </Paper>
        )}

        {/* Main form */}
        <Paper className={classes.paper}>
          <Typography variant="h6" className={classes.sectionTitle}>
            Informazioni Generali
          </Typography>

          <Grid container spacing={3}>
            {/* Riga 1: Cliente (2/3) + Stato (1/3) - come in Offerta */}
            <Grid item xs={12} md={8}>
              <Autocomplete
                freeSolo
                autoComplete
                id="ricerca-cliente"
                open={openCliente}
                onOpen={() => setOpenCliente(true)}
                onClose={() => setOpenCliente(false)}
                value={valueCliente}
                onChange={(event, newValue) => {
                  setValueCliente(newValue);
                  // Update provvigione with selected value or null
                  if (newValue && typeof newValue === 'object') {
                    // User selected from dropdown
                    setProvvigione(prev => ({
                      ...prev,
                      cliente: newValue.name,
                    }));
                  }
                }}
                onInputChange={(event, newInputValue) => {
                  setInputValueCliente(newInputValue);
                  // Update provvigione with free text input
                  setProvvigione(prev => ({
                    ...prev,
                    cliente: newInputValue,
                  }));
                }}
                getOptionSelected={(option, value) => option.name === value.name}
                getOptionLabel={option => (typeof option === 'string' ? option : option.name + ' - ' + option.stato)}
                options={optionsCliente}
                loading={loadingCliente}
                renderInput={params => (
                  <TextField
                    {...params}
                    label="Cerca cliente *"
                    variant="outlined"
                    required
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <React.Fragment>
                          {loadingCliente ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </React.Fragment>
                      ),
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl variant="outlined" className={classes.formControl}>
                <InputLabel>Stato</InputLabel>
                <Select name="stato_new" value={provvigione.stato_new || '0'} onChange={handleChange} label="Stato">
                  <MenuItem value="0">Attiva</MenuItem>
                  <MenuItem value="1">In contatto</MenuItem>
                  <MenuItem value="2">In corso/C.O.</MenuItem>
                  <MenuItem value="3">Provvigione/FT</MenuItem>
                  <MenuItem value="4">Rifiutata</MenuItem>
                  <MenuItem value="5">Archiviata</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Riga 2: Numero + Data + Agente (3 colonne) */}
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Numero Provvigione"
                name="numero"
                value={provvigione.numero || ''}
                onChange={handleChange}
                variant="outlined"
                disabled={true}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Data Creazione"
                name="data_creazione"
                type="date"
                value={provvigione.data_creazione}
                onChange={handleChange}
                variant="outlined"
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl variant="outlined" className={classes.formControl}>
                <InputLabel>Agente *</InputLabel>
                <Select
                  name="agente_id"
                  value={provvigione.agente_id}
                  onChange={handleChange}
                  label="Agente *"
                  required>
                  <MenuItem value="">
                    <em>Seleziona agente</em>
                  </MenuItem>
                  {agenti.map(agente => (
                    <MenuItem key={agente.id} value={agente.id}>
                      {agente.nome}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Riga 3: Descrizione (full width) */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Descrizione *"
                name="descrizione"
                value={provvigione.descrizione}
                onChange={handleChange}
                variant="outlined"
                multiline
                rows={3}
                required
              />
            </Grid>

            {/* Riga 4: Totale Offerta + Percentuale + Totale Provvigione (3 colonne) */}
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Totale Offerta"
                name="totale_offerta"
                type="number"
                value={provvigione.totale_offerta}
                onChange={handleChange}
                variant="outlined"
                InputProps={{
                  startAdornment: <Typography>€</Typography>,
                }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Percentuale"
                name="percentuale"
                type="number"
                value={provvigione.percentuale}
                onChange={handleChange}
                variant="outlined"
                InputProps={{
                  endAdornment: <Typography>%</Typography>,
                }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Totale Provvigione"
                name="totale_provvigione"
                type="number"
                value={provvigione.totale_provvigione}
                onChange={handleChange}
                variant="outlined"
                InputProps={{
                  startAdornment: <Typography>€</Typography>,
                }}
              />
            </Grid>

            {/* Riga 5: Note (full width) */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Note"
                name="note"
                value={provvigione.note}
                onChange={handleChange}
                variant="outlined"
                multiline
                rows={4}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Fornitori section */}
        <Paper className={classes.paper}>
          <Typography variant="h6" className={classes.sectionTitle}>
            Fornitori
          </Typography>
          <FornitoriTable
            pid={provvigione.id || 0}
            fornitori={fornitori}
            setFornitori={setFornitori}
            iva={provvigione.iva || 22}
          />
        </Paper>

        {/* Articoli section - hidden for now
        <Paper className={classes.paper}>
          <Typography variant="h6" className={classes.sectionTitle}>
            Articoli
          </Typography>
          <ArticoliForm
            pid={provvigione.id || 0}
            articoli={articoli}
            getArticles={setArticoli}
            dati={provvigione}
          />
        </Paper>
        */}

        {/* Allegati section - show always like Offerta does */}
        <Paper className={classes.paper}>
          <AllegatiProvvigione
            pid={provvigione.id || 0}
            allegati={allegati}
            setAllegati={setAllegati}
          />
        </Paper>

        {/* Action buttons at bottom */}
        <Paper className={classes.paper}>
          <Grid container spacing={2}>
            {/* Elimina button - only when archived */}
            {provvigione.archiviata !== 0 && provvigione.id > 0 && (
              <Grid item xs={12} sm={2} align="center">
                <Button
                  fullWidth
                  variant="contained"
                  color="secondary"
                  startIcon={<MdDeleteForever />}
                  onClick={handleDelete}
                  disabled={saving}
                  style={{ backgroundColor: '#d32f2f', color: 'white' }}>
                  Elimina
                </Button>
              </Grid>
            )}

            {/* Salva button */}
            <Grid item xs={12} sm={2} align="center">
              <Button
                fullWidth
                variant="contained"
                color="primary"
                startIcon={<MdSave />}
                onClick={handleSave}
                disabled={saving}>
                {saving ? 'Salvataggio...' : 'Salva'}
              </Button>
            </Grid>

            {/* Archivia/Ripristina button - only for existing provvigioni */}
            {provvigione.id > 0 && (
              <Grid item xs={12} sm={2} align="center">
                <Button
                  fullWidth
                  variant="contained"
                  color={provvigione.archiviata === 0 ? 'default' : 'secondary'}
                  startIcon={provvigione.archiviata === 0 ? <MdArchive /> : <MdUnarchive />}
                  onClick={handleArchive}
                  disabled={saving}>
                  {provvigione.archiviata === 0 ? 'Archivia' : 'Ripristina'}
                </Button>
              </Grid>
            )}

            {/* Crea Versione button - only for existing provvigioni */}
            {provvigione.id > 0 && (
              <Grid item xs={12} sm={3} align="center">
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  startIcon={<MdControlPointDuplicate />}
                  onClick={handleVersiona}
                  disabled={saving}>
                  Crea Versione
                </Button>
              </Grid>
            )}
          </Grid>
        </Paper>
      </Box>
    </PageContainer>
  );
}
